import dayjs from "dayjs";
import { isEqual } from "lodash";
import { isFieldEditable } from "../../ExploreTasking/ultis";

/**
 * Turns a saved order back into the dynamic form's `formData`, driven by the
 * provider's flow schema. Lives outside the page so the mapping — the part that
 * decides whether a field arrives filled or empty — can be tested on its own.
 */

// Build field mapping from schema's body_template
export const buildFieldMapping = (orderData: any, stepSchema: any) =>
{
   const mapping: Record<string, any> = {};

   // Get all field names from ui.order
   const fieldOrder = stepSchema?.ui?.order || [];
   const requestBody = orderData?.items?.[0]?.request_body || {};

   fieldOrder.forEach((field: string) =>
   {
      // Map field to value from order data
      // Priority order: request_body > top-level data
      if (field in requestBody)
      {
         mapping[field] = requestBody[field];
      } else if (field === "name" && orderData.name)
      {
         mapping[field] = orderData.name;
      } else if (field === "priority" && orderData.priority_id)
      {
         mapping[field] = orderData.priority_id;
      } else if (field === "reason" && orderData.reason)
      {
         mapping[field] = orderData.reason;
      } else if (field === "description" && orderData.description)
      {
         mapping[field] = orderData.description;
      } else if (field === "provider")
      {
         mapping[field] =
            orderData.provider?.code || orderData.provider?.name?.toLowerCase();
      } else if (field === "order_type")
      {
         mapping[field] = orderData.order_type || "tasking";
      } else
      {
         mapping[field] = undefined;
      }
   });

   return mapping;
};

// Transform order data to form data (dynamic, based on schema)
export const transformOrderToFormData = (orderData: any, workflowSchema: any) =>
{
   if (!orderData || !workflowSchema) return {};

   const formData: Record<string, any> = {};

   workflowSchema.steps?.forEach((step: any) =>
   {
      const fieldOrder = step?.ui?.order || [];
      const fieldOptions = step?.ui?.options || {};

      const mapping = buildFieldMapping(orderData, step);

      fieldOrder.forEach((field: string) =>
      {
         let value = mapping[field];
         const config = fieldOptions[field];

         if (config?.widget === "date-picker" || config?.widget === "date")
         {
            if (value)
            {
               if (typeof value === "number")
               {
                  formData[field] = dayjs(value);
               } else if (typeof value === "string")
               {
                  formData[field] = dayjs(value);
               } else
               {
                  formData[field] = value;
               }
            } else
            {
               formData[field] = null;
            }
         } else if (config?.widget === "range" || config?.widget === "slider")
         {
            formData[field] =
               value !== undefined && value !== null
                  ? parseFloat(value)
                  : (config.default ?? config.min ?? 0);
         } else if (
            (config?.widget === "select" || config?.widget === "radio") &&
            config.options
         )
         {
            // const optionValues = config.options.map((opt: any) => opt.value);
            // formData[field] = optionValues.includes(value)
            //    ? value
            //    : config.default || optionValues[0];

            const optionValues = config.options.map((opt: any) => opt.value);

            if (Array.isArray(value))
            {
               // multiple select
               const validValues = value.filter((v) => optionValues.includes(v));

               formData[field] =
                  validValues.length > 0
                     ? validValues
                     : config.default || [optionValues[0]];
            } else
            {
               // single select
               formData[field] = optionValues.includes(value)
                  ? value
                  : config.default || optionValues[0];
            }
         } else
         {
            // Every widget renders `value ?? config.default`, so a field the
            // order doesn't carry shows the schema default while formData stays
            // empty — and the form then refuses to save, saying a box the user
            // can plainly read a number in "is required". Seeding the default
            // keeps shown, validated and submitted the same value. The archive
            // edit form has always done this; this one only did it for
            // range/slider/select.
            formData[field] =
               value === undefined || value === null ? config?.default : value;
         }
      });
   });

   return formData;
};

/** Every field the flow declares in ui.options, whether or not it is locked. */
export const getDeclaredFields = (workflowSchema: any) =>
{
   const declared = new Set<string>();

   workflowSchema?.steps?.forEach((step: any) =>
   {
      Object.keys(step?.ui?.options || {}).forEach((key) => declared.add(key));
   });

   return declared;
};

export const getEditableFields = (workflowSchema: any) =>
{
   const editableFields = new Set<string>();

   workflowSchema?.steps?.forEach((step: any) =>
   {
      const options = step?.ui?.options || {};

      Object.keys(options).forEach((key) =>
      {
         const config = options[key];

         // Same rule the renderer uses, from the same place: a field carrying
         // no flag is editable, so it has to be sent. Demanding an explicit
         // `true` here is what silently dropped every flag-less field from the
         // request while the user was editing it on screen.
         if (isFieldEditable(config))
         {
            editableFields.add(key);
         }
      });
   });

   return editableFields;
};

/**
 * Whether the field still holds what it was loaded with. A date lives in the
 * form as a dayjs instance, and two dayjs objects for the same moment are not
 * the same object, so both sides drop to a timestamp before comparing; the deep
 * compare then covers arrays (multi-selects) and objects (geometry).
 */
const isUnchanged = (value: any, initial: any): boolean =>
{
   const asComparable = (input: any) =>
      dayjs.isDayjs(input) ? input.valueOf() : input;

   return isEqual(asComparable(value), asComparable(initial));
};

/**
 * The PATCH body: every field the flow declares in ui.options, doesn't lock, and
 * whose value the user actually changed since the order was loaded.
 *
 * `initialFormData` is the snapshot transformOrderToFormData() produced on load
 * — comparing against the raw order instead would mean comparing a dayjs to an
 * ISO string and a seeded default to nothing.
 */
export const transformFormDataToOrderPayload = (
   formData: any,
   initialFormData: any,
   workflowSchema: any,
   /** `debug` prints why each declared field was or wasn't included. */
   options: { debug?: boolean } = {},
) =>
{
   if (!workflowSchema) return {};

   const requestOrder: Record<string, any> = {};
   const editable = getEditableFields(workflowSchema);

   // Why each declared field did or didn't make it into the request. "It edits on
   // screen but never reaches the API" is the recurring puzzle on this form, and
   // guessing at it from the outside is slow — dev builds print the answer.
   const skipped: Record<string, string> = {};

   // ui.options is the whole gate now. `submit.body_template` used to be part of
   // it, which meant a field the form let the user edit was dropped in silence
   // whenever the template didn't happen to list it.
   getDeclaredFields(workflowSchema).forEach((fieldName) =>
   {
      if (!editable.has(fieldName))
      {
         skipped[fieldName] = "locked by schema (can_edit: false)";
         return;
      }

      let value = formData?.[fieldName];

      // Never set at all — leave the key out instead of sending an empty one.
      // A field the user deliberately cleared holds null or "", and still goes.
      if (value === undefined)
      {
         skipped[fieldName] = "no value in the form";
         return;
      }

      if (isUnchanged(value, initialFormData?.[fieldName]))
      {
         skipped[fieldName] = "unchanged since the order was loaded";
         return;
      }

      //  auto handle date (không hardcode field name)
      if (value && dayjs.isDayjs(value))
      {
         value = value.valueOf();
      }

      requestOrder[fieldName] = value;
   });

   if (options.debug && process.env.NODE_ENV !== "production")
   {
      // eslint-disable-next-line no-console
      console.debug(
         "[edit order] sending",
         Object.keys(requestOrder),
         "| skipped",
         skipped,
      );
   }

   return {
      ...requestOrder,
   };
};
